import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  DialogTitle,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import { TransactionReceiptDg, PriceLabel } from 'components';
import persianJs from 'persianjs';
import { makeStyles } from '@mui/styles';
import { withdrawalGuild, postCard, postSheba } from '../../services/services';
import createToast from 'components/Toast/Toast';
import { useBusiness } from 'hooks';
import { useCurrencyUnit } from 'hooks';

const useStyles = makeStyles((theme) => ({
  dialogTitleH2: {
    fontSize: 16,
  },
  warningSatna: {
    background: ' #A1AAE312',
    border: ' 2px solid #A1AAE380',
    padding: '0 10px',
    borderRadius: 10,
    marginBottom: 17,
    '& h4': {
      color: '#F44336',
    },
  },
  dialogMain: {
    '& .MuiDialog-paper': {
      borderRadius: '10px',
      overflowY: 'inherit',
      overflow: 'hidden',
    },
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::scrollbar-track': {
        width: '0',
        minWidth: '0',
      },
      '&::-moz-scrollbar-track': {
        width: '0',
        minWidth: '0',
      },
      '&::-webkit-scrollbar-track': {
        width: '0',
        minWidth: '0',
      },
      '&::-webkit-scrollbar': {
        width: '0',
        minWidth: '0',
      },
      '&::-webkit-scrollbar-thumb': {
        width: '0',
        minWidth: '0',
      },
      background: '#fff',
      '&.noneOverFlow': {
        overflowY: 'inherit',
      },
    },
    '& .MuiDialogActions-root': {
      justifyContent: 'flex-start',
      display: 'flex',
      flexDirection: 'row-reverse',
      padding: '20px',
      gap: '15px',
    },
    '& .MuiDialog-container': {
      width: '100vw',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  },
  dialog: {
    textAlign: 'left',
    direction: 'ltr',
    background: '#f3f3f3',
    padding: theme.spacing(2),
    lineHeight: 2,
  },
}));

const WithdrawalDialog = (props) => {
  const {
    onClose,
    selectedValue,
    open,
    navigate,
    saveAccountNumber = false,
    cardInfoAcc,
    limitPrice,
    setOpenDialogWarning,
  } = props;

  const currencyText = useCurrencyUnit(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [isOpenTransactionReceipt, setIsOpenTransactionReceipt] = useState(false);
  const businessInfo = useBusiness();

  // در لود صفحه درخواست  اطلاعات کسب و کار دریافت میشود
  useEffect(() => {
    setCurrentUserId(businessInfo?.userId || null);
  }, [businessInfo]);

  const businessData = useBusiness();

  const classes = useStyles();
  const handleClose = () => {
    onClose();
  };

  const handleWithdraw = async () => {
    let param;
    setIsLoadingBtn(true);
    try {
      param = {
        guildCode: selectedValue.guildCode,
        amount: selectedValue.amount,
        toolCode: selectedValue.toolCode,
        toolId: selectedValue.toolId.value ?? selectedValue?.toolId,
        uniqueId: selectedValue.uniqueId,
        reason: selectedValue?.reason,
        captchaId: selectedValue?.captchaId,
        captchaAnswer: selectedValue?.captchaAnswer,
        description: selectedValue?.description,
        paymentId: selectedValue?.paymentId.trim() || null,
        firstName: selectedValue?.name,
        lastName: selectedValue?.family,
      };
      !param['paymentId'] && delete param['paymentId'];
      if (saveAccountNumber) {
        await postItem(
          selectedValue?.toolId?.value ?? selectedValue?.toolId,
          selectedValue?.numberName,
          selectedValue.toolCode.toLowerCase().includes('card') ? 'card' : 'sheba',
        );
      }
      const res = await withdrawalGuild(param);
      setTimeout(() => {
        createToast('موفق', 'درخواست شما با موفقیت ارسال شد.', 'success');
        setContactId(res?.result?.id);
        setIsOpenTransactionReceipt(true);
        onClose(res?.result?.id);
        setIsLoadingBtn(false);
        if (cardInfoAcc === 'SETTLEMENT_TOOL_SATNA') {
          if (!limitPrice?.enableSatna) {
            setOpenDialogWarning(true);
          }
        }
      }, 3000);
    } catch (e) {
      setIsLoadingBtn(false);
      onClose();
    }
  };

  const postItem = async (accountNumber, accountName, type = 'card') => {
    const body =
      type === 'card'
        ? {
            cardNumber: persianJs(accountNumber).toEnglishNumber().toString(),
            name: accountName,
            cvv2: null,
            expiryMonth: null,
            expiryYear: null,
            order: null,
            tag: [],
          }
        : {
            sheba: accountNumber,
            name: accountName,
          };

    type === 'card' ? await postCard(body) : await postSheba(body);
  };

  return (
    <>
      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        fullWidth
        open={open}
        className={classes.dialogMain}
      >
        <DialogTitle className={classes.dialogTitle}>
          <h2 className={classes.dialogTitleH2}> تایید عملیات </h2>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.dialog}>
          {cardInfoAcc === 'SETTLEMENT_TOOL_SATNA' && !limitPrice?.enableSatna && (
            <div className={classes.warningSatna}>
              <h4 style={{ marginTop: '15px' }}>هشدار : </h4>
              <p style={{ marginTop: '5px', marginBottom: '20px' }}>
                ساعت ثبت درخواست انتقال وجه از طریق سامانه ساتنا (فقط روزهای کاری) تا ساعت{' '}
                {limitPrice?.tools?.satna?.endTime} می‌باشد.
              </p>
            </div>
          )}
          <Typography variant="body1" gutterBottom style={{ marginTop: '20px' }}>
            اطلاعات زیر را تایید و از درخواست خود اطمینان دارید :
          </Typography>

          <div className="">
            مبلغ قابل برداشت :
            <PriceLabel amount={selectedValue.amount} currency={currencyText}/>
          </div>
          <div className="">
            {selectedValue.toolCode === 'SETTLEMENT_TOOL_CARD'
              ? 'شماره کارت مقصد'
              : 'شماره شبای مقصد'}
            : {selectedValue?.toolId?.label ?? selectedValue?.toolId}
          </div>
          <div className="">
            نام و نام خانوادگی : {selectedValue.name + ' ' + selectedValue.family}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleWithdraw}
            disabled={isLoadingBtn}
            variant="contained"
            color="primary"
          >
            بلی
            {isLoadingBtn && <CircularProgress size={24} />}
          </Button>
          <Button onClick={handleClose} variant="contained">
            خیر
          </Button>
        </DialogActions>
      </Dialog>
      {/* دیالوگ مربوط به رسید تراکنش */}
      <TransactionReceiptDg
        userId={currentUserId}
        isReporty={true}
        documentId={contactId}
        isReportyType="cashout"
        isOpenTransactionReceipt={isOpenTransactionReceipt}
        redirectUrl="/SettlementGuild"
        setIsOpenTransactionReceipt={setIsOpenTransactionReceipt}
      />
    </>
  );
};

export default WithdrawalDialog;
